/**
 * Customer DeliveryPolicy eligible APIs (supersedes ShippingMethod — plan G13).
 * Flag OFF / missing policy → hide SHIPPING modality UX (S23).
 * Charge path is always 0 — never methodId / payable fixedChargeMinor.
 */
import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import type { ShippingCheckoutPayload } from './shippingAddressTypes.js';

/** Tenant-authored cs+en map (authoring SSOT). */
export interface PolicyLocalizedBlob {
  cs?: string | null;
  en?: string | null;
}

/** Local DTO matching DeliveryPolicy plan shape (backend may still be in flux). */
export interface CustomerDeliveryPolicyDTO {
  id?: number;
  etaMinBusinessDays: number;
  etaMaxBusinessDays: number;
  latestDeliveryBusinessDays: number;
  priceRangeDescription: PolicyLocalizedBlob;
  outOfBandFeeWarning: PolicyLocalizedBlob;
  mechanicsDescription: PolicyLocalizedBlob;
  active?: boolean;
  /**
   * ISO-2 allowlist (GoPay MoR §13). Empty + active policy = unrestricted (a).
   * Prefer API-projected codes; missing → [] (unrestricted when policy active).
   */
  allowedCountryCodes?: readonly string[] | null;
}

/**
 * Eligible response — hard never methods[] / methodId fee.
 * Hook name retained for call-site stability while payload reshapes to policy.
 */
export interface EligibleShippingMethodsDTO {
  shippingLightEnabled: boolean;
  policy: CustomerDeliveryPolicyDTO | null;
}

export interface ShippingQuoteDTO {
  shippingLightEnabled: true;
  fulfillmentMode: 'SHIPPING';
  linesSubtotalMinor: number;
  shippingChargeMinor: 0;
  serverTotalMinor: number;
  charge: {
    shippingChargeMinor: 0;
  };
}

export function useEligibleShippingMethods(
  salesPointId: number | null,
): UseQueryResult<EligibleShippingMethodsDTO, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const enabled = salesPointId !== null && salesPointId > 0;
  return useQuery({
    queryKey: customerQueryKeys.shippingMethods(tenantCode, salesPointId ?? 0),
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const raw = await api.request<EligibleShippingMethodsDTO & { methods?: unknown }>(
        `/customer-checkout/sales-points/${salesPointId}/shipping-methods`,
      );
      // Normalize flux payloads: ignore legacy methods[]; require policy shape.
      const policyRaw =
        raw.policy != null && typeof raw.policy === 'object' ? raw.policy : null;
      let policy: CustomerDeliveryPolicyDTO | null = null;
      if (policyRaw != null) {
        const allowRaw = (policyRaw as CustomerDeliveryPolicyDTO).allowedCountryCodes;
        const allowedCountryCodes = Array.isArray(allowRaw)
          ? allowRaw
              .filter((c): c is string => typeof c === 'string')
              .map((c) => c.trim().toUpperCase())
              .filter((c) => c.length === 2)
          : [];
        policy = {
          ...(policyRaw as CustomerDeliveryPolicyDTO),
          allowedCountryCodes,
        };
      }
      return {
        shippingLightEnabled: raw.shippingLightEnabled === true,
        policy,
      };
    },
  });
}

export function useQuoteShippingCharge(): UseMutationResult<
  ShippingQuoteDTO,
  Error,
  {
    salesPointId: number;
    currency: string;
    linesSubtotalMinor: number;
    shipping: ShippingCheckoutPayload;
  }
> {
  const api = useApiClient();
  return useMutation({
    mutationFn: (body) =>
      api.request<ShippingQuoteDTO>('/customer-checkout/shipping/quote', {
        method: 'POST',
        body,
      }),
  });
}

/** True when modality UX may be shown (flag ON + active DeliveryPolicy). */
export function isShippingModalityUxVisible(
  data: EligibleShippingMethodsDTO | null | undefined,
): boolean {
  return data?.shippingLightEnabled === true && data.policy != null;
}
